import { useEffect, useMemo, useRef, useState } from "react";
import { accountService, taskService } from "@miplatform/api";
import { HTTP_STATUSES, ISSUE_STATUSES } from "@miplatform/constants";
import { useBoolean } from "@miplatform/hooks";
import { toastService } from "@miplatform/toast";
import {
  IInvoiceItemResponse,
  IIssue,
  IStateManager,
  InvoiceItemVarianceType,
} from "@miplatform/types";
import { bugsnag, createUUID } from "@miplatform/utils";
import { QCCameraLineItemType } from "./QCCameraItem";

export type Invoice = {
  id?: string;
  orderId?: string;
  modifiedOn?: string;
  invoiceItems?: IInvoiceItemResponse[];
};

type ServiceResponse<T> = {
  status?: number;
  data?: T;
};

export type CloseoutAttempt = {
  unfilteredIssues?: IIssue[];
  issues?: IIssue[];
  orderId?: string;
  invoiceItems?: IInvoiceItemResponse[];
  home?: {
    id?: string;
  };
  assignment?: {
    assignmentTemplateName?: string;
  };
};

type UseCloseoutQCCameraViewModelParams = {
  attempt?: CloseoutAttempt;
  stateManager: IStateManager;
  getAttempt: () => void;
  onCancel: () => void;
};

const createLineItem = (name = ""): QCCameraLineItemType => {
  const tempId = createUUID();

  return {
    id: tempId,
    tempId,
    name,
    quantity: 1,
    unitPrice: 0,
    isNew: true,
  };
};

const toLineItem = (item: IInvoiceItemResponse): QCCameraLineItemType => ({
  id: item.id,
  name: item.name,
  quantity: item.quantity ?? 1,
  unitPrice: item.unitPrice ?? 0,
  modifiedOn: item.modifiedOn,
});

const getPersistedLineItems = (invoice?: Invoice | null) =>
  (invoice?.invoiceItems || [])
    .filter(
      (item) =>
        item.invoiceItemVarianceType !== InvoiceItemVarianceType.Variance
    )
    .map(toLineItem);

export const useCloseoutQCCameraViewModel = ({
  attempt,
  stateManager,
  getAttempt,
  onCancel,
}: UseCloseoutQCCameraViewModelParams) => {
  const invoiceRef = useRef<Invoice | null>(null);
  const invoiceRequestRef = useRef<Promise<Invoice | null> | null>(null);
  const hasSeededItemRef = useRef(false);
  const hasHydratedItemsRef = useRef(false);
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [billingCommitmentsTotal, setBillingCommitmentsTotal] = useState(0);
  const [isSummary, handleSummaryOpen, handleSummaryClose] = useBoolean();
  const [hasLoadedInvoice, setHasLoadedInvoice] = useState(false);
  const [localItems, setLocalItems] = useState<QCCameraLineItemType[]>([]);
  const [tempItems, setTempItems] = useState<QCCameraLineItemType[]>([]);
  const assignmentItemName = attempt?.assignment?.assignmentTemplateName || "";
  const [selectedItem, setSelectedItem] = useState<QCCameraLineItemType | null>(
    null
  );
  const assignmentName = assignmentItemName || "--";

  const items = useMemo(
    () => [...localItems, ...tempItems],
    [localItems, tempItems]
  );

  const hydrateInvoiceItems = (nextInvoice?: Invoice | null) => {
    if (hasHydratedItemsRef.current) {
      return;
    }

    hasHydratedItemsRef.current = true;
    const persistedItems = getPersistedLineItems(nextInvoice);
    setLocalItems(persistedItems);

    if (persistedItems.length > 0) {
      hasSeededItemRef.current = true;
    }
  };

  const lookupInvoice = () => {
    if (
      invoiceRef.current?.orderId &&
      invoiceRef.current.orderId === attempt?.orderId
    ) {
      return invoiceRef.current;
    }

    const invoiceItems = attempt?.invoiceItems ?? [];
    const invoiceId = invoiceItems.find((item) => item.invoiceId)?.invoiceId;

    if (!invoiceId || !attempt?.orderId) {
      return null;
    }

    const existingInvoice: Invoice = {
      id: invoiceId,
      orderId: attempt.orderId,
      invoiceItems,
    };

    invoiceRef.current = existingInvoice;
    setInvoice(existingInvoice);
    return existingInvoice;
  };

  useEffect(() => {
    hasSeededItemRef.current = false;
    hasHydratedItemsRef.current = false;
    invoiceRef.current = null;
    setInvoice(null);
    setLocalItems([]);
    setTempItems([]);
    setHasLoadedInvoice(false);

    if (!attempt?.orderId) {
      hasHydratedItemsRef.current = true;
      setHasLoadedInvoice(true);
      return;
    }

    const existingInvoice = lookupInvoice();
    hydrateInvoiceItems(existingInvoice);
    if (!hasSeededItemRef.current && assignmentItemName) {
      hasSeededItemRef.current = true;
      setTempItems([createLineItem(assignmentItemName)]);
    }
    setHasLoadedInvoice(true);
  }, [attempt?.orderId, attempt?.invoiceItems]);

  useEffect(() => {
    if (!hasLoadedInvoice || hasSeededItemRef.current || !assignmentItemName) {
      return;
    }
    hasSeededItemRef.current = true;
    setTempItems((currentItems) =>
      currentItems.length > 0
        ? currentItems
        : [createLineItem(assignmentItemName)]
    );
  }, [assignmentItemName, hasLoadedInvoice]);

  const hasUnsavedItem = items.some((item) => item.isNew || item.isSubmitting);
  const isNextDisabled = hasUnsavedItem || items.length === 0;
  const totalPrice = useMemo(
    () =>
      items.reduce((total, item) => {
        if (item.isNew) {
          return total;
        }

        return total + item.quantity * item.unitPrice;
      }, 0),
    [items]
  );
  const variance = billingCommitmentsTotal - totalPrice;

  const preBillInvoice = () => {
    if (Math.abs(variance) > 0.01) {
      toastService.error({
        text: "The payments total does not match the invoice total. Please adjust accordingly.",
      });
      return;
    }

    if (!attempt?.orderId) {
      return;
    }

    const { orderId } = attempt;

    stateManager.run(async () => {
      const orderResponse = (await taskService.order.getById({
        orderId,
      })) as { status?: number; data?: { modifiedOn?: string } };

      if (
        orderResponse.status !== HTTP_STATUSES.OK ||
        !orderResponse.data?.modifiedOn
      ) {
        return;
      }

      const response = (await taskService.order.preBillInvoices({
        orderId,
        modifiedOn: orderResponse.data.modifiedOn,
        toastMessageConfig: {
          success: {
            text: "Order prebilled successfully.",
          },
        },
      } as unknown as Parameters<typeof taskService.order.preBillInvoices>[0])) as {
        status?: number;
      };

      if (response.status === HTTP_STATUSES.NO_CONTENT) {
        getAttempt();
        onCancel();
      }
    });
  };

  const handleBack = () => {
    if (isSummary) {
      handleSummaryClose();
      return;
    }

    onCancel();
  };

  const handleNext = () => {
    if (isSummary) {
      preBillInvoice();
      return;
    }

    handleSummaryOpen();
  };

  const handleGetOrCreateInvoice = async () => {
    if (invoiceRef.current?.orderId) {
      return invoiceRef.current;
    }

    if (invoiceRequestRef.current) {
      return invoiceRequestRef.current;
    }

    const request = (async () => {
      const existingInvoice = lookupInvoice();

      if (existingInvoice?.orderId) {
        hydrateInvoiceItems(existingInvoice);
        return existingInvoice;
      }

      const id = createUUID();
      invoiceRef.current = { id };
      const createResponse = (await stateManager.run(() =>
        accountService.invoices.create({
          data: {
            id,
            orderId: attempt?.orderId,
            name: attempt?.assignment?.assignmentTemplateName,
          },
          toastMessageConfig: undefined,
        })
      )) as unknown as ServiceResponse<Invoice>;

      if (createResponse?.data?.id) {
        invoiceRef.current = createResponse.data;
        setInvoice(createResponse.data);
        return createResponse.data;
      }

      setInvoice(invoiceRef.current);
      return invoiceRef.current;
    })().catch((error) => {
      invoiceRequestRef.current = null;
      throw error;
    });

    invoiceRequestRef.current = request;

    return request;
  };

  const handleAddItem = () => {
    setTempItems((prev) => [...prev, createLineItem()]);
  };

  const handleCreateItem = async (updatedItem: QCCameraLineItemType) => {
    setTempItems((prev) =>
      prev.map((item) =>
        item.id === updatedItem.id ? { ...item, isSubmitting: true } : item
      )
    );

    try {
      const currentInvoice = await handleGetOrCreateInvoice();

      if (!currentInvoice?.id) {
        throw new Error("Failed to create invoice");
      }

      const response = (await stateManager.run(async () =>
        accountService.invoiceItems.create({
          data: {
            invoiceId: currentInvoice.id,
            name: updatedItem.name,
            quantity: updatedItem.quantity,
            unitPrice: updatedItem.unitPrice,
          },
          toastMessageConfig: {
            success: {
              text: "Invoice item created successfully.",
            },
          },
        })
      )) as unknown as ServiceResponse<IInvoiceItemResponse>;

      const createdItem = response?.data;

      if (response?.status === HTTP_STATUSES.CREATED && createdItem) {
        setLocalItems((prev) => [...prev, toLineItem(createdItem)]);
        setTempItems((prev) =>
          prev.filter((item) => item.id !== updatedItem.id)
        );
        return;
      }

      throw new Error("Failed to create invoice item");
    } catch (error) {
      setTempItems((prev) =>
        prev.map((item) =>
          item.id === updatedItem.id ? { ...item, isSubmitting: false } : item
        )
      );
      throw error;
    }
  };

  const handleUpdateExistingItem = async (
    updatedItem: QCCameraLineItemType
  ) => {
    setLocalItems((prev) =>
      prev.map((item) =>
        item.id === updatedItem.id ? { ...item, isSubmitting: true } : item
      )
    );

    try {
      const response = (await stateManager.run(async () =>
        accountService.invoiceItems.update({
          id: updatedItem.id,
          data: {
            name: updatedItem.name,
            quantity: updatedItem.quantity,
            unitPrice: updatedItem.unitPrice,
            modifiedOn: updatedItem.modifiedOn,
          },
          toastMessageConfig: {
            success: {
              text: "Invoice item updated successfully.",
            },
          },
        })
      )) as unknown as ServiceResponse<IInvoiceItemResponse>;

      if (response?.status === HTTP_STATUSES.OK && response.data) {
        const savedItem = toLineItem(response.data);
        setLocalItems((prev) =>
          prev.map((item) => (item.id === updatedItem.id ? savedItem : item))
        );
        return;
      }

      throw new Error("Failed to update invoice item");
    } catch (error) {
      setLocalItems((prev) =>
        prev.map((item) =>
          item.id === updatedItem.id ? { ...item, isSubmitting: false } : item
        )
      );
      throw error;
    }
  };

  const handleUpdateItem = async (updatedItem: QCCameraLineItemType) => {
    try {
      if (updatedItem.tempId) {
        await handleCreateItem(updatedItem);
        return;
      }

      await handleUpdateExistingItem(updatedItem);
    } catch (error) {
      bugsnag.notify("Error updating invoice item:", error);
      throw error;
    }
  };

  const handleDeleteRequest = (item: QCCameraLineItemType) => {
    if (item.tempId || item.isNew) {
      setTempItems((prev) => prev.filter((entry) => entry.id !== item.id));
      return;
    }

    setSelectedItem(item);
  };

  const handleDeleteModalClose = () => {
    setSelectedItem(null);
  };

  const handleDeleteItem = async () => {
    if (!selectedItem) {
      return;
    }

    const itemToDelete = selectedItem;
    setSelectedItem(null);
    setLocalItems((prev) =>
      prev.map((item) =>
        item.id === itemToDelete.id ? { ...item, isSubmitting: true } : item
      )
    );

    try {
      const response = (await stateManager.run(async () =>
        accountService.invoiceItems.delete({
          id: itemToDelete.id,
          modifiedOn: itemToDelete.modifiedOn,
          toastMessageConfig: {
            success: {
              text: "Invoice item deleted successfully.",
            },
          },
        })
      )) as unknown as ServiceResponse<unknown>;

      if (response?.status === HTTP_STATUSES.OK) {
        setLocalItems((prev) =>
          prev.filter((item) => item.id !== itemToDelete.id)
        );
        return;
      }

      throw new Error("Failed to delete invoice item");
    } catch (error) {
      setLocalItems((prev) =>
        prev.map((item) =>
          item.id === itemToDelete.id ? { ...item, isSubmitting: false } : item
        )
      );
      bugsnag.notify("Error deleting invoice item:", error);
    }
  };

  const issues = attempt?.unfilteredIssues || attempt?.issues;

  const isIncompleteIssues = useMemo(
    () =>
      issues?.some(
        (issue: IIssue) => issue.status === ISSUE_STATUSES.CONFIRMED
      ),
    [issues]
  );

  return {
    invoice,
    items,
    assignmentName,
    isSummary,
    isNextDisabled,
    hasUnsavedItem,
    totalPrice,
    variance,
    billingCommitmentsTotal,
    selectedItem,
    setBillingCommitmentsTotal,
    handleGetOrCreateInvoice,
    handleAddItem,
    handleUpdateItem,
    handleDeleteRequest,
    handleDeleteModalClose,
    handleDeleteItem,
    handleBack,
    handleNext,
    isIncompleteIssues,
  };
};
